import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useHistory } from 'react-router';
import dayjs from 'dayjs';
import isToday from 'dayjs/plugin/isToday';
import _ from 'lodash';
import Tooltip from '@mui/material/Tooltip';

import FixedLoader from '../../../components/FixedLoader/FixedLoader';
import Api from '../../../api';
import useActionApi from '../../../hooks/useActionApi';
import { formatProfileHygienistName, getPaymentTerms, getPaymentMethod } from '../../../utils';
import { formatFirstLicensedLabel } from '../../../utils/educationCredentials';
import { JobPropsWithHygienist, PositionProps, SkillsProps, UserAccountProps } from 'utils/types';
import { Language } from 'api/Api';
import { convertDentalOfficeStatus } from '../../hygienist/ShiftDetailPage/components';
import { getPerformanceScoreDisplay } from 'utils/performanceScoreDisplay';
import { getOfficeHistoryCopy, useOfficeHistory } from 'utils/officeHistory';
import { openExternalUrlFromLink } from 'utils/nativeApp';

// Components
import DentistNavbar from '../../../components/DentistNavbar';
import Popup from '../../../components/Popup';
import TalentResume from '../../../components/TalentResume';
import { CollapsableContainer } from '../../../components/CollapsableContainer';
import Skills from '../CustomOfferDetailsPage/components/Skills';
import WorkExperience from '../CustomOfferDetailsPage/components/WorkExperience';
import { AdminTasksDisplay } from '../../hygienist/AvailableJobPage/components/AdminTasksDisplay';
import { CancelShiftModal } from './components/CancelShift';
import { SimpleModal } from '../../../components/Modal';
import {
    CannotCallModal,
    CHANGE_SHIFT_HOURS_POLICY,
    ModifyShiftModal,
    PhoneNumberModal,
    ShiftStartedModal,
} from './components/Modals';
import { StyledButton } from '../../../components/Buttons/DefaultButton/DefaultButton.styles';
import { usePersistedState } from '../../../hooks';
import { entities } from '../../../reduxStore';

// Icons
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RoomOutlinedIcon from '@mui/icons-material/RoomOutlined';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import ChatOutlinedIcon from '@mui/icons-material/ChatOutlined';
import DescriptionIcon from '@mui/icons-material/Description';
import LocalPhoneOutlinedIcon from '@mui/icons-material/LocalPhoneOutlined';
import WarningIcon from '@mui/icons-material/Warning';
import NoteIcon from '@mui/icons-material/Note';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';

import heartFilled from '../../../images/heartFilled.svg';
import heartUnfilled from '../../../images/heartUnfilled.svg';
import TypographyFilled from '../../../images/Typography.png';
import WorkFilled from '../../../images/WorkFilled.png';
import AccountCircleFilled from '../../../images/AccountCircleFilled.png';

import styles from './ConfirmedOfferPage.module.scss';

dayjs.extend(isToday);

const DIRECT_PAYMENT_POLICY = 'https://www.tempstars.com/direct_payment';
const BREAKS_POLICY = 'https://support.tempstars.com/hc/en-us/articles/360051065833-What-is-paid-time-and-unpaid-time';
const START_TIME_POLICY = 'https://support.tempstars.com/hc/en-us/articles/360052402253-Arrival-preparation-Time';

type HygienistWithMembershipSummary = NonNullable<JobPropsWithHygienist['hygienist']> & {
    membershipSummary?: {
        officeStatusLabel: string | null;
        reliabilityLabel?: string | null;
        feedbackLabel?: string | null;
        reliabilityPercentile?: number | null;
        feedbackPercentile?: number | null;
    } | null;
};

const RELIABILITY_TOOLTIP = "Talent Reliability Score: A measure of how dependably this talent shows up for shifts they've booked — based on their history of completed shifts versus cancellations (with last-minute cancellations weighing more heavily and recent behavior counting more than older activity).";
const FEEDBACK_TOOLTIP = "Overall Feedback Score: A rolling average of the star ratings this talent has received from dental offices across past shifts (covering clinical skills, professionalism, communication, time management, and rate satisfaction) — giving you an at-a-glance sense of how other offices have experienced working with them.";

const isMobileTooltip = () => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia?.('(hover: none), (pointer: coarse)').matches || window.innerWidth <= 768;
};

const getScoreValueClass = (label: string) => {
    switch (label) {
        case 'Outstanding':
        case 'Very Good':
        case 'Great':
            return styles.scoreValueGreen;
        case 'Good':
            return styles.scoreValueBlue;
        case 'Fair':
            return styles.scoreValueYellow;
        case 'Low':
            return styles.scoreValueCoral;
        default:
            return styles.scoreValueMuted;
    }
};

// ============ MAIN COMPONENT ============

const DentistConfirmedOfferPage: React.FC = () => {
    const history = useHistory();
    
    // ===== PAGE STATE =====
    const [trackData, setTrackData] = useState<any>(null);
    const [isFavouriteHygienist, setIsFavouriteHygienist] = useState<boolean>(false);
    const [updatingFavourite, setUpdatingFavourite] = useState<boolean>(false);
    const [isCannotCallModalOpen, setIsCannotCallModalOpen] = useState(false);
    const [isPhoneModalOpen, setIsPhoneModalOpen] = useState(false);
    const [isResumeModalOpen, setIsResumeModalOpen] = useState(false);
    const [isModifyModalOpen, setIsModifyModalOpen] = useState(false);
    const [isShiftStartedModalOpen, setIsShiftStartedModalOpen] = useState(false);
    const [showShortNoticeWarning, setShowShortNoticeWarning] = useState(false);
    const [phone, setPhone] = useState<string | null>(null);
    const [workExperience, setWorkExperience] = useState<PositionProps[]>([]);
    const [latestWorkExperience, setLatestWorkExperience] = useState<PositionProps>();
    const [apiSkills, setApiSkills] = useState<SkillsProps[]>([]);

    const locationState = useMemo(
        () => history.location.state as any,
        [history.location.state]
    );

    const { id: jobId, job: jobData } = locationState || {};
    const [job, setJob] = useState<JobPropsWithHygienist>(jobData || ({} as any));

    const hygienist = job?.hygienist as HygienistWithMembershipSummary | undefined;
    const officeHistory = useOfficeHistory(Number(job?.hygienistId) || null, Number(job?.locationId) || null);

    const [user] = usePersistedState<UserAccountProps>({
        entityName: entities.user,
    });

    // Refs to prevent infinite loops
    const hasFetchedFavourite = useRef<number | null>(null);

    // ===== API CALLS =====
    const handleJobDetailsSuccess = useCallback((jobObj: any) => {
        if (_.isEmpty(jobObj)) return;
        setJob(jobObj);
        if (jobObj.hygienistId && jobObj.locationId) {
            getResumeData({ hygienistId: jobObj.hygienistId, locationId: jobObj.locationId });
        }
    }, []);

    const handleResumeDataSuccess = useCallback((hygienistData: any) => {
        // T2-6520: getJobDetails already deletes hygienist.phone outside the API's
        // ±2h window (authoritative UTC gate on Shift.postedStart). getResumeData
        // returns phone unconditionally, so preserve the API-gated phone here rather
        // than re-deriving availability in the browser timezone.
        setJob(job => ({ ...job, hygienist: { ...hygienistData, phone: (job as any)?.hygienist?.phone } }));
        setIsFavouriteHygienist(hygienistData?.isFavouriteDentist || false);
        setWorkExperience(hygienistData?.workExperience || []);
    }, []);

    const [getJobDetailsLoading, , getJobDetails] = useActionApi({
        apiMethod: Api.getJobDetails,
        onSuccess: handleJobDetailsSuccess
    });

    const [getSkillsLoading, , getSkills] = useActionApi({
        apiMethod: Api.getSkills,
        onSuccess: (skills: SkillsProps[]) => {
            if (_.isEmpty(skills)) return;
            setApiSkills(skills);
        },
    });

    const [getResumeDataLoading, , getResumeData] = useActionApi({
        apiMethod: Api.getResumeData,
        onSuccess: handleResumeDataSuccess
    });

    // ===== COMPUTED VALUES =====
    const shouldFetchJob = useMemo(() => _.isEmpty(jobData) && jobId, [jobData, jobId]);
    const isJobEmpty = useMemo(() => _.isEmpty(job), [job]);
    const hasValidShifts = useMemo(() => job?.shifts && job.shifts.length > 0, [job?.shifts]);

    const formattedPostedDate = useMemo(() => {
        return job.postedOn ? dayjs(job.postedOn).format('dddd, MMM D') : '';
    }, [job.postedOn]);

    const formattedShiftDate = useMemo(() => {
        return job.shifts?.[0]?.shiftDate ? dayjs(job.shifts[0].shiftDate).format('dddd, MMM D') : '';
    }, [job.shifts]);

    const shiftTime = useMemo(() => {
        if (!job.shifts?.[0]) return '';
        return `${job.shifts[0].postedStart} - ${job.shifts[0].postedEnd}`;
    }, [job.shifts]);

    const avgRecall = useMemo(() => job.location?.avgApptTime || '30 min', [job.location?.avgApptTime]);

    const baseHourlyRate = useMemo(() => {
        return job?.hourlyRate;
    }, [job]);

    const hourlyRate = useMemo(() => {
        return `$${baseHourlyRate}/hr`;
    }, [baseHourlyRate]);

    const paymentDescription = useMemo(() => {
        const terms = getPaymentTerms(job.paymentTerms);
        const method = job.paymentMethod
            ? `typically via ${getPaymentMethod(job.paymentMethod, job.location?.country)}`
            : '';
        return `Office Pays: ${terms} ${method}`;
    }, [job.paymentTerms, job.paymentMethod, job.location?.country]);

    const safetyGuidelinesText = useMemo(() => {
        return `This office has confirmed they meet or exceed the ${job.location?.province || ''} guidelines for workplace safety and infection control.`;
    }, [job.location?.province]);

    const hasEndorsements = useMemo(() => {
        return !!job.location?.ppeEndorsed || !!job.location?.friendlyEndorsed || !!job.location?.equipEndorsed;
    }, [job.location]);

    const tempingRoleLabel = useMemo(() => {
        const t = Number(job?.type);
        if (t === 0) return 'Dental hygienist';
        if (t === 1) return 'Dental assistant';
        return 'Temping professional';
    }, [job?.type]);

    const downtimePaidUnpaid = useMemo(() => {
        if (!job.shifts?.[0]) return null;
        return Number((job.shifts[0] as any).gapUnpaid) === 1 ? 'Unpaid' : 'Paid';
    }, [job.shifts]);

    const doTopNote = useMemo(() => {
        const fn = hygienist?.firstName?.trim() || 'this professional';
        const prac = job.location?.practiceName?.trim() || 'your office';
        return `This shift with ${fn} is confirmed and booked at ${prac}.`;
    }, [hygienist?.firstName, job.location?.practiceName]);

    const paymentAgreementLine = useMemo(() => {
        const fn = hygienist?.firstName?.trim() || 'the talent';
        const terms = getPaymentTerms(job.paymentTerms);
        const method = job.paymentMethod
            ? getPaymentMethod(job.paymentMethod, job.location?.country)
            : 'the agreed payment method';
        return { fn, terms, method };
    }, [job.paymentTerms, job.paymentMethod, job.location?.country, hygienist?.firstName]);

    const professionalLicense = useMemo(() => {
        if (_.isEmpty(hygienist)) return '';
        return formatProfileHygienistName(hygienist, hygienist?.skills, { layout: 'stacked' });
    }, [hygienist]);

    const languages = hygienist?.languages ?? [];
    const languagesToShow = languages.filter((lang: Language) => lang.code !== 'en') ?? [];

    const hasDocument = !!(job?.location as any)?.photoUrl;

    // Track data for analytics
    const memoizedTrackData = useMemo(() => {
        if (_.isEmpty(job) || !hasValidShifts) return null;
        return {
            isInstabookEnabled: !!job.Instabook,
            recommendedHourlyRate: job.proposedRate,
            currencyCode: job.location?.country ? 'USD' : 'CAD',
            dentalOfficeLocationId: job.locationId,
            dentalOfficeId: job.dentistId,
            dentalOfficeStatus: convertDentalOfficeStatus(job.location?.status ?? 0),
            shiftStartDate: new Date(
                job.startDate?.replace(/-/g, '/') + ' ' + job.shifts[0].postedStart
            ).toISOString(),
            shiftEndDate: new Date(
                job.startDate?.replace(/-/g, '/') + ' ' + job.shifts[0].postedEnd
            ).toISOString(),
            shiftId: job.shifts?.[0]?.id,
            shiftPostDate: job.postedOn ? new Date(job.postedOn).toISOString() : ''
        };
    }, [job, hasValidShifts]);

    // ===== EFFECTS =====
    useEffect(() => {
        if (shouldFetchJob && jobId) {
            getJobDetails(jobId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shouldFetchJob, jobId]);

    useEffect(() => {
        if (!_.isEmpty(jobData) && jobData.hygienistId && jobData.locationId) {
            getResumeData({ hygienistId: jobData.hygienistId, locationId: jobData.locationId });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (job?.hygienist?.phone) {
            const justNumbers = job.hygienist.phone.replace(/\D/g, '');
            const phoneNumber = justNumbers.substr(0, 3) + '-' + justNumbers.substr(3, 3) + '-' + justNumbers.substr(6, 4);
            setPhone(phoneNumber);
        }
        if (job?.hygienist?.skills) {
            getSkills({ type: job.hygienist.type, state: job.hygienist.province });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [job?.hygienist]);

    useEffect(() => {
        setTrackData(memoizedTrackData);
    }, [memoizedTrackData]);

    useEffect(() => {
        if (workExperience) {
            let latest = workExperience.find((experience) => experience.current);
            if (!latest) {
                latest = workExperience.sort((a, b) => {
                    return dayjs(b.toDate).valueOf() - dayjs(a.toDate).valueOf();
                })[0];
            }
            setLatestWorkExperience(latest);
        }
    }, [workExperience]);

    // ===== HANDLERS =====
    const handleBackNavigation = useCallback(() => {
        history.push({ pathname: 'dentistHome' });
    }, [history]);

    const handleToggleFavourite = async () => {
        // Favourite status is read from resume data
    };

    const handleViewMap = () => {
        const officeAddress = job.location?.lat + ',+' + job.location?.lon;
        const url = `https://www.google.com/maps/dir/${officeAddress}`;
        if ((window as any).Cordova) {
            (window as any).cordova.InAppBrowser.open(url, '_system', 'location=yes');
        } else {
            window.open(url, '_blank', 'location=yes');
        }
    };

    const handleChat = useCallback(() => {
        history.push({ pathname: 'messenger', state: { job } });
    }, [history, job]);

    const handleDocumentView = useCallback(() => {
        const photoUrl = (job?.location as any)?.photoUrl;
        if (!photoUrl) return;
        try {
            const fileUrl = photoUrl;
            if ((window as any).cordova) {
                (window as any).cordova.InAppBrowser.open(fileUrl, '_system', 'location=yes');
            } else {
                window.open(fileUrl, '_blank');
            }
        } catch (error) {
            console.error('Error handling document:', error);
            window.location.href = photoUrl;
        }
    }, [job?.location]);

    const handleCancelJob = useCallback(() => {
        // Cancel shift modal will handle this
    }, []);

    const goCall = () => {
        // T2-6520: the API only returns the Talent's phone within its ±2h window
        // (deletes it otherwise). If we have a phone, reveal it in a popup instead of
        // auto-dialing (which the browser blocks); otherwise show "not yet available".
        if (phone) {
            setIsPhoneModalOpen(true);
        } else {
            setIsCannotCallModalOpen(true);
        }
    };

    const goToModifyJob = () => {
        history.push('modifyJob', { job });
    };

    /** T2-6106 / T2-6107: evaluate "now" at click time — started blocks first, else CA + less-than-24h warning */
    const handleChangeShiftTimeClick = (e: React.MouseEvent) => {
        e.preventDefault();
        const postedStart = job?.shifts?.[0]?.postedStart;
        if (!job?.startDate || !postedStart) {
            setShowShortNoticeWarning(false);
            setIsModifyModalOpen(true);
            return;
        }
        const start = dayjs(job.startDate.replace(/-/g, '/') + ' ' + postedStart);
        const now = dayjs();
        if (!start.isAfter(now)) {
            setIsShiftStartedModalOpen(true);
            return;
        }
        const less24h = start.diff(now, 'hour') < 24;
        const isCanadian = user?.dentist?.countryStr === 'CA' || job.location?.country === 0;
        setShowShortNoticeWarning(!!(isCanadian && less24h));
        setIsModifyModalOpen(true);
    };

    const bookAnotherHandler = () => {
        history.push('postJob', { date: dayjs(job.startDate).toISOString() });
    };

    const handleViewResume = () => {
        if (!hygienist) return;
        if (hygienist.resumeUrl) {
            if ((window as any).Cordova) {
                (window as any).cordova.InAppBrowser.open(hygienist.resumeUrl, '_system', 'location=yes');
            } else {
                window.open(hygienist.resumeUrl, '_blank');
            }
        } else {
            setIsResumeModalOpen(true);
        }
    };

    // ===== LOADING STATE =====
    const isLoading = getJobDetailsLoading || getResumeDataLoading || getSkillsLoading;

    if (isJobEmpty && !getJobDetailsLoading) {
        return (
            <div className={styles.pageContainer}>
                <FixedLoader loading={getJobDetailsLoading} />
            </div>
        );
    }

    if (_.isEmpty(hygienist)) {
        return (
            <div className={styles.pageContainer}>
                <FixedLoader loading={isLoading} />
            </div>
        );
    }
    const officeMembershipStatusLabel = hygienist?.membershipSummary?.officeStatusLabel || null;
    const reliabilityLabel = hygienist?.membershipSummary?.reliabilityLabel || null;
    const feedbackLabel = hygienist?.membershipSummary?.feedbackLabel || null;
    const reliabilityPercentile = hygienist?.membershipSummary?.reliabilityPercentile ?? null;
    const feedbackPercentile = hygienist?.membershipSummary?.feedbackPercentile ?? null;

    const renderHelpIcon = (tooltipText: string) => {
        const icon = (
            <HelpOutlineIcon
                className={styles.scoreHelpIcon}
                fontSize="small"
                onClick={(event) => {
                    if (!isMobileTooltip()) return;
                    event.preventDefault();
                    event.stopPropagation();
                    alert(tooltipText);
                }}
            />
        );

        return isMobileTooltip()
            ? icon
            : (
                <Tooltip
                    title={tooltipText}
                    arrow
                    placement="top"
                    classes={{ tooltip: styles.scoreTooltip }}
                >
                    {icon}
                </Tooltip>
            );
    };

    const renderScoreRow = (
        label: string,
        valueLabel: string | null,
        percentile: number | null,
        fallback: string,
        tooltipText: string
    ) => {
        const scoreDisplay = getPerformanceScoreDisplay(valueLabel, percentile);

        return (
            <div className={styles.scoreRow}>
                {renderHelpIcon(tooltipText)}
                <span className={styles.scoreLabel}>{label}:</span>
                {scoreDisplay ? (
                    <span className={`${styles.scoreValue} ${getScoreValueClass(scoreDisplay.label)}`}>
                        {scoreDisplay.text}
                    </span>
                ) : (
                    <span className={styles.scoreFallback}>{fallback}</span>
                )}
            </div>
        );
    };

    const performanceScoreRows = (
        <div className={styles.performanceScoreRows}>
            {renderScoreRow('Reliability', reliabilityLabel, reliabilityPercentile, 'Building reliability history', RELIABILITY_TOOLTIP)}
            {renderScoreRow('Overall Feedback', feedbackLabel, feedbackPercentile, 'Building feedback history', FEEDBACK_TOOLTIP)}
            {officeHistory && (
                <div className={`${styles.scoreRow} ${styles.officeHistoryRow}`} data-testid="confirmed-office-history">
                    <span className={styles.scoreLabel}>History With Your Office:</span>
                    <span className={styles.scoreFallback}>{getOfficeHistoryCopy(officeHistory)}</span>
                </div>
            )}
        </div>
    );

    return (
        <div className={styles.pageContainer}>
            {/* ===== MODALS ===== */}
            <SimpleModal isOpen={isCannotCallModalOpen}>
                <CannotCallModal firstName={hygienist.firstName} onCloseButtonClick={() => setIsCannotCallModalOpen(false)} />
            </SimpleModal>

            <SimpleModal isOpen={isPhoneModalOpen}>
                <PhoneNumberModal firstName={hygienist.firstName} phone={phone || ''} onCloseButtonClick={() => setIsPhoneModalOpen(false)} />
            </SimpleModal>

            <SimpleModal isOpen={isModifyModalOpen}>
                <ModifyShiftModal
                    firstName={hygienist.firstName}
                    onSubmit={goToModifyJob}
                    onCloseButtonClick={() => setIsModifyModalOpen(false)}
                    showShortNoticeWarning={showShortNoticeWarning}
                />
            </SimpleModal>

            <ShiftStartedModal
                isOpen={isShiftStartedModalOpen}
                onClose={() => setIsShiftStartedModalOpen(false)}
            />

            <Popup opened={isResumeModalOpen} onClose={() => setIsResumeModalOpen(false)}>
                <TalentResume hygienist={hygienist} onClose={() => setIsResumeModalOpen(false)} />
            </Popup>

            {/* ===== HEADER ===== */}
            <DentistNavbar
                title="Booked/Confirmed Shift"
                showGetTempButton={false}
            />

            {/* ===== MAIN CONTENT ===== */}
            {!isJobEmpty && (
                <main className={styles.mainContent}>
                    {/* ===== SUBHEADER ===== */}
                    <div className={styles.subheader}>
                        <button 
                            className={styles.backButton} 
                            onClick={handleBackNavigation}
                            aria-label="Go back"
                            title="Go back"
                        >
                            <ArrowBackIcon />
                        </button>
                        <div className={styles.subheaderActions}>
                            <div className={styles.bookedBadge}>
                                <div className={styles.bookedDot}></div>
                                Booked
                            </div>
                            <button className={styles.locationButton} onClick={handleViewMap} aria-label="View location on map" title="View location on map">
                                <RoomOutlinedIcon />
                                Location
                            </button>
                            <button
                                className={styles.heartButton}
                                onClick={handleToggleFavourite}
                                disabled={true}
                                title={isFavouriteHygienist ? 'Favourite hygienist' : 'Not a favourite'}
                            >
                                <img
                                    src={isFavouriteHygienist ? heartFilled : heartUnfilled}
                                    alt={isFavouriteHygienist ? 'Favourite hygienist' : 'Not a favourite'}
                                    className={styles.heartIcon}
                                />
                            </button>
                        </div>
                    </div>

                    {/* Main Content Box */}
                    <div className={styles.mainContentBox}>
                        <p className={styles.confirmationNote}>{doTopNote}</p>
                        <p className={styles.metaRow}>
                            <strong>Temping role:</strong> {tempingRoleLabel}
                            {downtimePaidUnpaid != null && (
                                <>
                                    {' '}
                                    &middot; <strong>Downtime:</strong> {downtimePaidUnpaid}
                                </>
                            )}
                        </p>
                        <p className={styles.paymentAgreement}>
                            You&apos;ve agreed to pay {paymentAgreementLine.fn} within {paymentAgreementLine.terms} via{' '}
                            {paymentAgreementLine.method}.{' '}
                            <a
                                href={DIRECT_PAYMENT_POLICY}
                                target="_system"
                                rel="noopener noreferrer"
                                className="external no-fastclick"
                                id="dentist-confirmed-payment-policy-link"
                                data-testid="dentist-confirmed-payment-policy-link"
                                aria-label="Open payment policy"
                                content-desc="dentist-confirmed-payment-policy-link"
                                onClick={(event) => openExternalUrlFromLink(event.nativeEvent, DIRECT_PAYMENT_POLICY)}
                            >
                                Payment policy
                            </a>
                            .
                        </p>
                        {/* Shift Details Card - Light Grey Background */}
                        <div className={styles.shiftDetailsCard}>
                            <div className={styles.shiftDateRow}>
                                <div className={styles.shiftDate}>
                                    <CalendarTodayOutlinedIcon className={styles.shiftDateIcon} />
                                    {formattedShiftDate}
                                </div>
                            </div>
                            <div className={styles.shiftLocationRow}>
                                <div className={styles.shiftLocationColumn}>
                                    <div className={styles.shiftLocation}>
                                        {job.location?.address},
                                        <br />
                                        {job.location?.city}, {job.location?.province} {job.location?.postalCode}
                                    </div>
                                    <div className={styles.bookedRateRow}>
                                        <AttachMoneyIcon className={styles.bookedRateIcon} />
                                        <span className={styles.bookedRateLabel}>Booked Rate:</span>
                                        <span className={styles.bookedRateValue}>{hourlyRate}</span>
                                    </div>
                                </div>
                                <div className={styles.shiftTimes}>
                                    <div className={styles.shiftTimeItem}>
                                        <span className={styles.shiftTimeLabel}>Start:</span>
                                        <span className={styles.shiftTimeValue}>{job.shifts?.[0]?.postedStart}</span>
                                    </div>
                                    <div className={styles.shiftTimeItem}>
                                        <span className={styles.shiftTimeLabel}>End:</span>
                                        <span className={styles.shiftTimeValue}>{job.shifts?.[0]?.postedEnd}</span>
                                    </div>
                                    {job.shifts?.[0]?.firstPatientTime && (
                                        <div
                                            className={styles.shiftTimeItem}
                                            id="dentist-confirmed-first-patient-time"
                                            data-testid="dentist-confirmed-first-patient-time"
                                        >
                                            <span className={styles.shiftTimeLabel}>First patient time:</span>
                                            <span className={styles.shiftTimeValue}>
                                                {job.shifts[0].firstPatientTime}
                                            </span>
                                        </div>
                                    )}
                                    <div className={styles.changeShiftLinks}>
                                        <a
                                            href="#"
                                            className={styles.changeShiftTimeLink}
                                            id="change-shift-time-link"
                                            data-testid="change-shift-time-link"
                                            onClick={handleChangeShiftTimeClick}
                                        >
                                            Change Shift Time
                                        </a>
                                        <a
                                            href={CHANGE_SHIFT_HOURS_POLICY}
                                            className={`${styles.changeShiftPolicyLink} external no-fastclick`}
                                            target="_system"
                                            rel="noopener noreferrer"
                                            id="dentist-confirmed-change-shift-policy-link"
                                            data-testid="dentist-confirmed-change-shift-policy-link"
                                            aria-label="Open change shift policy"
                                            content-desc="dentist-confirmed-change-shift-policy-link"
                                            onClick={(event) => openExternalUrlFromLink(event.nativeEvent, CHANGE_SHIFT_HOURS_POLICY)}
                                        >
                                            Change Shift Policy
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Reception/Admin duties + attire (T2-6509) */}
                        {job.type === 2 && job.adminTasks && (
                            <AdminTasksDisplay
                                adminTasks={job.adminTasks}
                                attire={job.adminTasks.attire}
                            />
                        )}

                        {/* Booked Shift Details Card - White Background */}
                        <div className={styles.bookedOfferCard}>
                            <div className={styles.bookedOfferHeader}>
                                <h2 className={styles.bookedOfferTitle}>Booked Shift Details</h2>
                            </div>

                            {/* Hygienist Profile Section with Action Buttons */}
                            <div className={styles.profileSection}>
                                <div className={styles.profileMain}>
                                    {hygienist.photoUrl && (
                                        <img 
                                            src={hygienist.photoUrl} 
                                            alt={professionalLicense}
                                            className={styles.profileImage}
                                        />
                                    )}
                                    <div className={styles.profileInfo}>
                                        <div className={styles.profileNameRow}>
                                            <h3 className={styles.profileName}>{professionalLicense}</h3>
                                            <img
                                                src={isFavouriteHygienist ? heartFilled : heartUnfilled}
                                                alt={isFavouriteHygienist ? 'Remove from favourites' : 'Add to favourites'}
                                                className={styles.heartIconSmall}
                                            />
                                            {officeMembershipStatusLabel && (
                                                <span className={styles.membershipStatusBadge}>{officeMembershipStatusLabel}</span>
                                            )}
                                        </div>
                                        {hygienist.graduationYear && hygienist.school && (
                                            <p className={styles.graduatedText}>
                                                Graduated {hygienist.graduationYear} from {hygienist.school}
                                            </p>
                                        )}
                                        {formatFirstLicensedLabel(hygienist.firstLicensedYear, hygienist.firstLicensedMonth) && (
                                            <p className={styles.graduatedText}>
                                                {formatFirstLicensedLabel(hygienist.firstLicensedYear, hygienist.firstLicensedMonth)}
                                            </p>
                                        )}
                                        {hygienist.isLeftHanded !== undefined && (
                                            <p className={styles.leftHanded}>
                                                {hygienist.firstName} is {hygienist.isLeftHanded ? 'Left' : 'Right'} handed
                                            </p>
                                        )}
                                    </div>

                                    {/* Action Buttons - stacked vertically on the right */}
                                    <div className={styles.actionButtonsRow}>
                                        <StyledButton
                                            variant="outlined"
                                            color="primary"
                                            size="small"
                                            startIcon={<LocalPhoneOutlinedIcon />}
                                            className={`${styles.callButtonPill} callButton`}
                                            onClick={goCall}
                                        >
                                            Call
                                        </StyledButton>
                                        <StyledButton
                                            variant="outlined"
                                            color="primary"
                                            size="small"
                                            startIcon={<ChatOutlinedIcon />}
                                            className={`${styles.chatButtonPill} chatButton`}
                                            onClick={handleChat}
                                        >
                                            Chat Now
                                        </StyledButton>
                                        <StyledButton
                                            variant="outlined"
                                            color="primary"
                                            size="small"
                                            startIcon={<DescriptionIcon />}
                                            className={`${styles.viewResumeButtonPill} resumeButton`}
                                            onClick={handleViewResume}
                                        >
                                            View Resume
                                        </StyledButton>
                                    </div>
                                </div>

                                {/* Reliability / Overall Feedback scores + ratings (full width) */}
                                <div className={styles.ratingsAndScores}>
                                    {performanceScoreRows}
                                    {(hygienist.numRatedJobs ?? 0) > 0 ? (
                                        <div className={styles.ratingsSection}>
                                            <div className={styles.stars}>
                                                {[1, 2, 3, 4, 5].map((star) => (
                                                    <span
                                                        key={star}
                                                        className={
                                                            star <= Math.floor(hygienist.starScore || 0)
                                                                ? styles.starFilled
                                                                : styles.starEmpty
                                                        }
                                                    >
                                                        ★
                                                    </span>
                                                ))}
                                            </div>
                                            <span className={styles.ratingsCount}>
                                                {hygienist.numRatedJobs ?? 0} {(hygienist.numRatedJobs ?? 0) === 1 ? 'Rating' : 'Ratings'}
                                            </span>
                                        </div>
                                    ) : (
                                        <div className={styles.ratingsSection}>
                                            <span className={styles.noRatings}>No Rated Shifts yet</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Shift Notes Section */}
                        {job.shiftNotes && job.shiftNotes.trim() && (
                            <div className={styles.shiftNotesCard}>
                                <div className={styles.shiftNotesHeader}>
                                    <NoteIcon className={styles.shiftNotesIcon} />
                                    <span className={styles.shiftNotesTitle}>Shift Notes</span>
                                </div>
                                <div className={styles.shiftNotesContent}>
                                    {job.shiftNotes}
                                </div>
                            </div>
                        )}

                        {/* Skills & Qualification Section */}
                        {(hygienist?.CDHONumber || hygienist?.skills?.length || languagesToShow.length) && (
                            <CollapsableContainer
                                title="Skills & Qualification (self-reported)"
                                defaultIsOpen={false}
                                icon={TypographyFilled}
                                backgroundColor="grey"
                                noBorder={true}
                            >
                                {hygienist?.CDHONumber && (
                                    <p className={styles.licenseInfo}>
                                        License #: {hygienist.CDHONumber}
                                    </p>
                                )}
                                {languagesToShow.length > 0 && (
                                    <>
                                        <p className={styles.sectionLabel}>Languages:</p>
                                        <p className={styles.sectionValue}>
                                            {languagesToShow.map((l: any) => l.name).join(', ')}
                                        </p>
                                    </>
                                )}
                                {hygienist?.skills && hygienist.skills.length > 0 && hygienist.type !== undefined && (
                                    <>
                                        <p className={styles.sectionLabel}>Training & Certifications received:</p>
                                        <Skills 
                                            apiSkills={apiSkills} 
                                            skills={hygienist.skills} 
                                            type={hygienist.type} 
                                            province={hygienist.province}
                                        />
                                    </>
                                )}
                            </CollapsableContainer>
                        )}

                        {/* Work Experience Section */}
                        {workExperience?.length > 0 && (
                            <CollapsableContainer
                                title="Work Experience"
                                defaultIsOpen={false}
                                icon={WorkFilled}
                                backgroundColor="grey"
                                noBorder={true}
                                preview={latestWorkExperience && <WorkExperience experiences={[latestWorkExperience]} />}
                                fullPreview={true}
                            >
                                <WorkExperience experiences={workExperience} />
                            </CollapsableContainer>
                        )}

                        {/* About Me Section */}
                        {hygienist?.about && (
                            <CollapsableContainer
                                title="About Me"
                                defaultIsOpen={true}
                                icon={AccountCircleFilled}
                                backgroundColor="grey"
                                noBorder={true}
                                preview={hygienist.about}
                            >
                                <p className={styles.aboutText}>{hygienist.about}</p>
                            </CollapsableContainer>
                        )}

                        {/* Friendly Reminder Section */}
                        <div className={styles.reminderSection}>
                            <div className={styles.reminderHeader}>
                                <WarningIcon className={styles.warningIcon} />
                                <span className={styles.reminderTitle}>a friendly reminder</span>
                            </div>
                            <p className={styles.reminderText}>
                                Standard placement fees apply to shifts booked with {hygienist?.firstName} - 
                                whether arranged on the app or through direct contact
                            </p>
                        </div>

                        {/* Important Notes Section */}
                        <div className={styles.importantNotesSection}>
                            <div className={styles.importantNotesTitle}>Important Notes</div>
                            <div className={styles.importantNotesContent}>
                                <div className={styles.noteItem}>
                                    <strong>Lunch and Breaks:</strong> Generally lunches and breaks are unpaid time.
                                    We know a workday at a dental office is unpredictable. But do ensure {hygienist?.firstName} gets an
                                    appropriate break/lunch.
                                    If the schedule is tight, discuss with {hygienist?.firstName} at the beginning of the shift so that expectations can be set.
                                    <a
                                        href={BREAKS_POLICY}
                                        target="_system"
                                        rel="noopener noreferrer"
                                        className={`${styles.noteLink} external no-fastclick`}
                                        id="dentist-confirmed-breaks-policy-link"
                                        data-testid="dentist-confirmed-breaks-policy-link"
                                        aria-label="Open breaks and lunches policy"
                                        content-desc="dentist-confirmed-breaks-policy-link"
                                        onClick={(event) => openExternalUrlFromLink(event.nativeEvent, BREAKS_POLICY)}
                                    >
                                        (TempStars policy on breaks and lunches)
                                    </a>
                                </div>
                                <div className={styles.noteItem}>
                                    <strong>Start Time:</strong> The "Start Time" is the time that {hygienist?.firstName} should be at your office, changed into scrubs and ready to work.
                                    There is no expectation for them to be ready early for unpaid orientation. Ensure your Start Time takes prep/orientation time into consideration.
                                    It makes for a hard day if your Start Time is the time your first patient is scheduled to be sitting in the chair.
                                    <a
                                        href={START_TIME_POLICY}
                                        target="_system"
                                        rel="noopener noreferrer"
                                        className={`${styles.noteLink} external no-fastclick`}
                                        id="dentist-confirmed-start-time-policy-link"
                                        data-testid="dentist-confirmed-start-time-policy-link"
                                        aria-label="Open start time policy"
                                        content-desc="dentist-confirmed-start-time-policy-link"
                                        onClick={(event) => openExternalUrlFromLink(event.nativeEvent, START_TIME_POLICY)}
                                    >
                                        (Learn more about Start Times)
                                    </a>
                                </div>
                                <div className={styles.noteItem}>
                                    <strong>Directly Arranged Shifts:</strong> If it goes well with {hygienist?.firstName} and want them to work future shifts, that's ok.
                                    But remember you agreed to
                                    <a
                                        href={DIRECT_PAYMENT_POLICY}
                                        target="_system"
                                        rel="noopener noreferrer"
                                        className={`${styles.noteLink} external no-fastclick`}
                                        id="dentist-confirmed-direct-connections-policy-link"
                                        data-testid="dentist-confirmed-direct-connections-policy-link"
                                        aria-label="Open direct connections policy"
                                        content-desc="dentist-confirmed-direct-connections-policy-link"
                                        onClick={(event) => openExternalUrlFromLink(event.nativeEvent, DIRECT_PAYMENT_POLICY)}
                                    >
                                        (TempStars Policy for Direct Connections)
                                    </a>
                                    , so our placement fee applies to those shifts, as per the policy you agreed to.
                                </div>
                                <div className={styles.noteItem}>
                                    <strong>Hiring a TempStars Temp:</strong> We don't charge for permanent hires: so if you have an opening on your team and want to hire {hygienist?.firstName} to your employee payroll...amazing!
                                    We don't charge hiring or finders fees - we're just happy for the great connection.
                                    You're welcome - it's one of the benefits of working with a company owned and lead by a practicing dentist.
                                </div>
                            </div>
                        </div>

                        {/* Bottom Action Buttons */}
                        <div className={styles.bottomButtons}>
                            <button 
                                className={styles.bookAnotherButton} 
                                onClick={bookAnotherHandler}
                                aria-label="Book another temp for this date"
                                title="Book another temp for this date"
                            >
                                Book Another Temp for this Date
                            </button>
                        </div>
                    </div>
                    {/* End Main Content Box */}

                    {/* Cancel Shift Modal - renders its own button */}
                    <CancelShiftModal
                        job={job}
                        onSubmit={() => history.push('loading')}
                    />

                </main>
            )}

            <FixedLoader loading={isLoading} />
        </div>
    );
};

export default DentistConfirmedOfferPage;
